import { isMarketingPath } from '~/utils/marketingRoutes'

/**
 * The browser / PWA chrome colour for the app (MAR-1316). It is the frame colour: `--color-sidebar`
 * in `tailwind.css` (pinned to it by `themeColorHead.test.ts`).
 *
 * `nuxt.config.ts` ships two `theme-color` tags scoped by `prefers-color-scheme`, which follow the
 * OS. An explicit in-app Appearance setting that differs from the OS cannot be seen by a media
 * query, so this swaps it at runtime.
 */
export const FRAME_THEME_COLOR = { light: '#F8F8F8', dark: '#111111' } as const

export type ThemeColorMeta = { name: 'theme-color', content: string, media: string }

/**
 * The `theme-color` tags to push over the config ones, or `[]` to leave the config ones alone.
 *
 * - `system`: the media-scoped tags already track the OS, and `resolved` is always `light` during
 *   SSR, so overriding would be wrong there.
 * - Public marketing pages are always light with their own colours: never overridden.
 * - An explicit setting: BOTH media variants carry the resolved frame colour, so the same
 *   `(prefers-color-scheme: …)` keys replace the config tags (head dedupe) whatever the OS says.
 */
export function themeColorMeta(
  preference: 'light' | 'dark' | 'system',
  resolved: 'light' | 'dark',
  path: string,
): ThemeColorMeta[] {
  if (preference === 'system' || isMarketingPath(path)) return []
  const content = FRAME_THEME_COLOR[resolved]
  return [
    { name: 'theme-color', content, media: '(prefers-color-scheme: light)' },
    { name: 'theme-color', content, media: '(prefers-color-scheme: dark)' },
  ]
}
